/**
 * Tab de Resumen Fiscal - Alineado con modelos de Hacienda (303, 111)
 */
import React, { useState, useEffect } from 'react';
import { Calculator, RefreshCw, ChevronDown, FileText, Users, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { API_URL, formatNumber } from './finanzasUtils';

const FiscalTab = ({ headers, añoSeleccionado }) => {
  const [resumenFiscal, setResumenFiscal] = useState(null);
  const [cargandoFiscal, setCargandoFiscal] = useState(false);
  const [trimestresExpandidos, setTrimestresExpandidos] = useState({});

  const cargarResumenFiscal = async () => {
    setCargandoFiscal(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/resumen-fiscal?anio=${añoSeleccionado}`, { headers });
      const data = await res.json();
      setResumenFiscal(data);
    } catch (e) {
      console.error('Error cargando resumen fiscal:', e);
    } finally {
      setCargandoFiscal(false);
    }
  };

  useEffect(() => {
    cargarResumenFiscal();
  }, [añoSeleccionado]);

  const toggleTrimestre = (t) => {
    setTrimestresExpandidos(prev => ({ ...prev, [t]: !prev[t] }));
  };

  const totalesAnio = resumenFiscal?.totales_anio;

  // Calcular acumulado YTD (hasta el mes actual si el año seleccionado es el actual)
  const calcularYTD = () => {
    if (!resumenFiscal?.trimestres) return null;
    const hoy = new Date();
    const esAnioActual = añoSeleccionado === hoy.getFullYear();
    const ultimoMes = esAnioActual ? hoy.getMonth() + 1 : 12;
    const camposSuma = [
      'base_imponible_emitidas', 'iva_repercutido', 'iva_4', 'iva_10', 'iva_21',
      'num_facturas_emitidas',
      'base_imponible_soportadas', 'iva_soportado', 'num_facturas_soportadas',
      'cargos_sin_factura_base', 'cargos_sin_factura_iva', 'num_cargos_sin_factura',
      'retenciones_irpf_facturas', 'retenciones_irpf_nominas',
      'seguridad_social_empresa', 'nominas_brutas',
    ];
    const ytd = {};
    camposSuma.forEach(k => { ytd[k] = 0; });
    let empleadosMaxYTD = 0;
    resumenFiscal.trimestres.forEach(t => {
      (t.meses || []).forEach(m => {
        const mesNum = parseInt((m.mes || '').split('-')[1] || '0', 10);
        if (mesNum >= 1 && mesNum <= ultimoMes) {
          camposSuma.forEach(k => { ytd[k] += (m[k] || 0); });
          empleadosMaxYTD = Math.max(empleadosMaxYTD, m.empleados_activos || 0);
        }
      });
    });
    ytd.iva_a_pagar = (ytd.iva_repercutido || 0) - (ytd.iva_soportado || 0) - (ytd.cargos_sin_factura_iva || 0);
    ytd.irpf_total = (ytd.retenciones_irpf_facturas || 0) + (ytd.retenciones_irpf_nominas || 0);
    ytd.empleados_activos = empleadosMaxYTD;
    ytd.resultado_neto = (ytd.base_imponible_emitidas || 0)
      - (ytd.base_imponible_soportadas || 0)
      - (ytd.cargos_sin_factura_base || 0)
      - (ytd.nominas_brutas || 0)
      - (ytd.seguridad_social_empresa || 0);
    ytd._mes_hasta = ultimoMes;
    ytd._es_anio_actual = esAnioActual;
    return ytd;
  };

  const ytd = calcularYTD();
  // Tarjetas principales = YTD; proyección anual aparte abajo.
  const totales = ytd || totalesAnio;
  const nombresMeses = ['', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="w-5 h-5 text-purple-600" />
            Resumen Fiscal {añoSeleccionado}
            {ytd?._es_anio_actual && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                Acumulado a {nombresMeses[ytd._mes_hasta]}
              </span>
            )}
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Modelo 303 (IVA) · Modelo 111 (IRPF) · Seguridad Social
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Cifras acumuladas desde el 1 de enero hasta hoy. Mas abajo veras la proyeccion al cierre del ano.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={cargarResumenFiscal} disabled={cargandoFiscal} data-testid="fiscal-calcular-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${cargandoFiscal ? 'animate-spin' : ''}`} />
          {cargandoFiscal ? 'Calculando...' : 'Recalcular'}
        </Button>
      </CardHeader>
      <CardContent>
        {!resumenFiscal ? (
          <div className="text-center py-8">
            <Calculator className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Cargando resumen fiscal...</p>
          </div>
        ) : (
          <div className="space-y-6">

            {/* === MODELO 303 - IVA === */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-sm text-slate-700 uppercase tracking-wide">Modelo 303 - IVA Anual</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="fiscal-iva-anual">
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-emerald-600 font-medium">Base Imp. Emitidas</p>
                  <p className="text-lg font-bold text-emerald-700" data-testid="fiscal-base-emitidas">
                    {formatNumber(totales?.base_imponible_emitidas || 0)}
                  </p>
                  <p className="text-xs text-emerald-500">{totales?.num_facturas_emitidas || 0} facturas</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium">IVA Repercutido</p>
                  <p className="text-lg font-bold text-blue-700" data-testid="fiscal-iva-repercutido">
                    {formatNumber(totales?.iva_repercutido || 0)}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                  <p className="text-xs text-red-600 font-medium">Base Imp. Soportadas</p>
                  <p className="text-lg font-bold text-red-700" data-testid="fiscal-base-soportadas">
                    {formatNumber((totales?.base_imponible_soportadas || 0) + (totales?.cargos_sin_factura_base || 0))}
                  </p>
                  <p className="text-xs text-red-500">
                    {totales?.num_facturas_soportadas || 0} fact. + {totales?.num_cargos_sin_factura || 0} cargos
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                  <p className="text-xs text-orange-600 font-medium">IVA Soportado</p>
                  <p className="text-lg font-bold text-orange-700" data-testid="fiscal-iva-soportado">
                    {formatNumber((totales?.iva_soportado || 0) + (totales?.cargos_sin_factura_iva || 0))}
                  </p>
                </div>
                <div className={`rounded-lg p-3 border ${(totales?.iva_a_pagar || 0) >= 0 ? 'bg-purple-50 border-purple-200' : 'bg-teal-50 border-teal-200'}`}>
                  <p className={`text-xs font-medium ${(totales?.iva_a_pagar || 0) >= 0 ? 'text-purple-600' : 'text-teal-600'}`}>
                    {(totales?.iva_a_pagar || 0) >= 0 ? 'IVA a Pagar' : 'IVA a Compensar'}
                  </p>
                  <p className={`text-lg font-bold ${(totales?.iva_a_pagar || 0) >= 0 ? 'text-purple-700' : 'text-teal-700'}`} data-testid="fiscal-iva-pagar">
                    {formatNumber(Math.abs(totales?.iva_a_pagar || 0))}
                  </p>
                </div>
              </div>
            </div>

            {/* === MODELO 111 - IRPF y SS === */}
            {(totales?.irpf_total > 0 || totales?.seguridad_social_empresa > 0 || totales?.nominas_brutas > 0) && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-purple-600" />
                  <h3 className="font-semibold text-sm text-slate-700 uppercase tracking-wide">Modelo 111 - Retenciones IRPF · SS · Nominas</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="fiscal-irpf-ss">
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                    <p className="text-xs text-purple-600 font-medium">IRPF Nominas</p>
                    <p className="text-lg font-bold text-purple-700">{formatNumber(totales?.retenciones_irpf_nominas || 0)}</p>
                    <p className="text-xs text-purple-500">Rend. del trabajo</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                    <p className="text-xs text-indigo-600 font-medium">IRPF Profesionales</p>
                    <p className="text-lg font-bold text-indigo-700">{formatNumber(totales?.retenciones_irpf_facturas || 0)}</p>
                    <p className="text-xs text-indigo-500">Fact. autonomos (15%)</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-3 border border-pink-100">
                    <p className="text-xs text-pink-600 font-medium">SS Empresa</p>
                    <p className="text-lg font-bold text-pink-700">{formatNumber(totales?.seguridad_social_empresa || 0)}</p>
                    <p className="text-xs text-pink-500">~{(resumenFiscal.detalle_ss?.porcentaje_empresa || 30.57).toFixed(2)}% bruto</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <p className="text-xs text-slate-600 font-medium">Nominas Brutas</p>
                    <p className="text-lg font-bold text-slate-700">{formatNumber(totales?.nominas_brutas || 0)}</p>
                    <p className="text-xs text-slate-500">{totales?.empleados_activos || 0} empleados</p>
                  </div>
                  <div className={`rounded-lg p-3 border ${(totales?.resultado_neto || 0) >= 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                    <p className={`text-xs font-medium ${(totales?.resultado_neto || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      Resultado Neto
                    </p>
                    <p className={`text-lg font-bold ${(totales?.resultado_neto || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatNumber(totales?.resultado_neto || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* === DESGLOSE TRIMESTRAL === */}
            <div>
              <h3 className="font-semibold text-sm text-slate-700 uppercase tracking-wide mb-3">Desglose por Trimestres</h3>
              <div className="space-y-2">
                {resumenFiscal.trimestres?.map((t) => {
                  const totalIvaSop = (t.iva_soportado || 0) + (t.cargos_sin_factura_iva || 0);
                  const expanded = trimestresExpandidos[t.trimestre];
                  return (
                    <div key={t.trimestre} className="border rounded-lg overflow-hidden" data-testid={`fiscal-trimestre-${t.trimestre}`}>
                      <button
                        className="w-full px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition-colors"
                        onClick={() => toggleTrimestre(t.trimestre)}
                        data-testid={`fiscal-toggle-${t.trimestre}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-purple-700 bg-purple-50 px-2.5 py-0.5 rounded text-sm">{t.label}</span>
                          <span className="text-sm text-slate-500">{t.periodo}</span>
                          <span className="text-xs text-slate-400">({t.num_facturas_emitidas} emit. · {t.num_facturas_soportadas} sop.)</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right flex gap-4">
                            <span className="text-xs">
                              Base: <span className="font-semibold text-emerald-600">{formatNumber(t.base_imponible_emitidas || 0)}</span>
                            </span>
                            <span className="text-xs">
                              IVA: <span className={`font-semibold ${t.iva_a_pagar >= 0 ? 'text-purple-600' : 'text-teal-600'}`}>
                                {t.iva_a_pagar >= 0 ? '' : '-'}{formatNumber(Math.abs(t.iva_a_pagar || 0))}
                              </span>
                            </span>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {expanded && (
                        <div className="border-t bg-slate-50 p-4 space-y-4">
                          {/* IVA del trimestre */}
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Modelo 303 - IVA</p>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                              <div>
                                <p className="text-slate-500 text-xs">Base Imp. Emitidas</p>
                                <p className="font-semibold text-emerald-700">{formatNumber(t.base_imponible_emitidas || 0)}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 text-xs">IVA Repercutido</p>
                                <p className="font-semibold text-blue-700">{formatNumber(t.iva_repercutido || 0)}</p>
                                {(t.iva_4 > 0 || t.iva_10 > 0 || t.iva_21 > 0) && (
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    {t.iva_4 > 0 && `4%: ${formatNumber(t.iva_4)} `}
                                    {t.iva_10 > 0 && `10%: ${formatNumber(t.iva_10)} `}
                                    {t.iva_21 > 0 && `21%: ${formatNumber(t.iva_21)}`}
                                  </p>
                                )}
                              </div>
                              <div>
                                <p className="text-slate-500 text-xs">Base Imp. Soportadas</p>
                                <p className="font-semibold text-red-700">{formatNumber((t.base_imponible_soportadas || 0) + (t.cargos_sin_factura_base || 0))}</p>
                                <p className="text-xs text-slate-400">{t.num_facturas_soportadas} fact. + {t.num_cargos_sin_factura} cargos</p>
                              </div>
                              <div>
                                <p className="text-slate-500 text-xs">IVA Soportado</p>
                                <p className="font-semibold text-orange-700">{formatNumber(totalIvaSop)}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 text-xs">{t.iva_a_pagar >= 0 ? 'A Ingresar' : 'A Compensar'}</p>
                                <p className={`font-bold ${t.iva_a_pagar >= 0 ? 'text-purple-700' : 'text-teal-700'}`}>
                                  {formatNumber(Math.abs(t.iva_a_pagar || 0))}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Nominas y SS */}
                          {(t.nominas_brutas > 0 || t.seguridad_social_empresa > 0) && (
                            <div className="border-t pt-3">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Modelo 111 - IRPF / SS</p>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                                <div>
                                  <p className="text-slate-500 text-xs">Nominas Brutas</p>
                                  <p className="font-semibold">{formatNumber(t.nominas_brutas || 0)}</p>
                                  <p className="text-xs text-slate-400">{t.empleados_activos} empleados</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">IRPF Nominas</p>
                                  <p className="font-semibold text-purple-700">{formatNumber(t.retenciones_irpf_nominas || 0)}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">IRPF Profesionales</p>
                                  <p className="font-semibold text-indigo-700">{formatNumber(t.retenciones_irpf_facturas || 0)}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">SS Empresa</p>
                                  <p className="font-semibold text-pink-700">{formatNumber(t.seguridad_social_empresa || 0)}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Resultado Trimestre</p>
                                  <p className={`font-bold ${t.resultado_neto >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                    {formatNumber(t.resultado_neto || 0)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Empleados contados en el trimestre con desglose IRPF */}
                          {t.nominas_brutas > 0 && t.meses && t.meses.length > 0 && (() => {
                            const ultimoMes = t.meses[t.meses.length - 1];
                            const detalle = ultimoMes.empleados_detalle || [];
                            const totalIrpfMes = detalle.reduce((s, e) => s + (e.irpf_retenido || 0), 0);
                            const totalSalMes = detalle.reduce((s, e) => s + (e.salario_bruto || 0), 0);
                            return (
                              <div className="border-t pt-3">
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                                  Detalle por empleado ({ultimoMes.nombre_mes}) - retencion IRPF Modelo 111
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-slate-500 border-b">
                                        <th className="text-left py-1 font-medium">Empleado</th>
                                        <th className="text-right py-1 font-medium">Salario bruto</th>
                                        <th className="text-right py-1 font-medium">IRPF %</th>
                                        <th className="text-right py-1 font-medium">IRPF retenido</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detalle.map((e, i) => (
                                        <tr key={i} className="border-b border-slate-100">
                                          <td className="py-1 text-slate-700">{e.nombre || 'Sin nombre'}</td>
                                          <td className="text-right">{formatNumber(e.salario_bruto || 0)}</td>
                                          <td className="text-right text-slate-500">{(e.retencion_irpf_pct || 0).toFixed(1)}%</td>
                                          <td className="text-right text-purple-700 font-medium">{formatNumber(e.irpf_retenido || 0)}</td>
                                        </tr>
                                      ))}
                                      <tr className="font-semibold bg-purple-50">
                                        <td className="py-1.5 px-1">Total mes</td>
                                        <td className="text-right px-1">{formatNumber(totalSalMes)}</td>
                                        <td></td>
                                        <td className="text-right text-purple-700 px-1">{formatNumber(totalIrpfMes)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Detalle meses */}
                          <div className="border-t pt-3">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Detalle Mensual</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-500 border-b">
                                    <th className="text-left py-1.5 font-medium">Mes</th>
                                    <th className="text-right py-1.5 font-medium">Base Emit.</th>
                                    <th className="text-right py-1.5 font-medium">IVA Rep.</th>
                                    <th className="text-right py-1.5 font-medium">Base Sop.</th>
                                    <th className="text-right py-1.5 font-medium">IVA Sop.</th>
                                    <th className="text-right py-1.5 font-medium">Nominas</th>
                                    <th className="text-right py-1.5 font-medium">SS Emp.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {t.meses?.map(m => (
                                    <tr key={m.mes} className="border-b border-slate-100">
                                      <td className="py-1.5 font-medium text-slate-700">{m.nombre_mes}</td>
                                      <td className="text-right text-emerald-700">{formatNumber(m.base_imponible_emitidas || 0)}</td>
                                      <td className="text-right text-blue-700">{formatNumber(m.iva_repercutido || 0)}</td>
                                      <td className="text-right text-red-700">{formatNumber((m.base_imponible_soportadas || 0) + (m.cargos_sin_factura_base || 0))}</td>
                                      <td className="text-right text-orange-700">{formatNumber((m.iva_soportado || 0) + (m.cargos_sin_factura_iva || 0))}</td>
                                      <td className="text-right">{formatNumber(m.nominas_brutas || 0)}</td>
                                      <td className="text-right text-pink-700">{formatNumber(m.seguridad_social_empresa || 0)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* === PROYECCION AL CIERRE DEL ANIO === */}
            {totalesAnio && ytd?._es_anio_actual && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-amber-600" />
                  <h3 className="font-semibold text-sm text-slate-700 uppercase tracking-wide">
                    Proyeccion al cierre del ano (12 meses)
                  </h3>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
                    Estimado
                  </span>
                </div>
                <p className="text-xs text-slate-400 mb-3">
                  Asume el ritmo actual de facturacion y el salario vigente de cada empleado durante todo el ano. No es vinculante.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">Base Imp. Emitidas</p>
                    <p className="text-base font-bold text-emerald-700">{formatNumber(totalesAnio.base_imponible_emitidas || 0)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">IVA repercutido</p>
                    <p className="text-base font-bold text-blue-700">{formatNumber(totalesAnio.iva_repercutido || 0)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">Nominas brutas anuales</p>
                    <p className="text-base font-bold text-slate-700">{formatNumber(totalesAnio.nominas_brutas || 0)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">IRPF nominas anual</p>
                    <p className="text-base font-bold text-purple-700">{formatNumber(totalesAnio.retenciones_irpf_nominas || 0)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">SS empresa anual</p>
                    <p className="text-base font-bold text-pink-700">{formatNumber(totalesAnio.seguridad_social_empresa || 0)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">Coste personal total</p>
                    <p className="text-base font-bold text-slate-700">
                      {formatNumber((totalesAnio.nominas_brutas || 0) + (totalesAnio.seguridad_social_empresa || 0))}
                    </p>
                  </div>
                  <div className={`rounded-lg p-3 border ${(totalesAnio.iva_a_pagar || 0) >= 0 ? 'bg-purple-50 border-purple-200' : 'bg-teal-50 border-teal-200'}`}>
                    <p className={`text-xs font-medium ${(totalesAnio.iva_a_pagar || 0) >= 0 ? 'text-purple-600' : 'text-teal-600'}`}>
                      {(totalesAnio.iva_a_pagar || 0) >= 0 ? 'IVA a pagar anual' : 'IVA a compensar anual'}
                    </p>
                    <p className={`text-base font-bold ${(totalesAnio.iva_a_pagar || 0) >= 0 ? 'text-purple-700' : 'text-teal-700'}`}>
                      {formatNumber(Math.abs(totalesAnio.iva_a_pagar || 0))}
                    </p>
                  </div>
                  <div className={`rounded-lg p-3 border ${(totalesAnio.resultado_neto || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <p className={`text-xs font-medium ${(totalesAnio.resultado_neto || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      Resultado neto anual
                    </p>
                    <p className={`text-base font-bold ${(totalesAnio.resultado_neto || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatNumber(totalesAnio.resultado_neto || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Nota de cargos estimados */}
            {(totales?.num_cargos_sin_factura > 0) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800" data-testid="fiscal-aviso-cargos">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>
                  <strong>{totales.num_cargos_sin_factura} cargos estructurales</strong> sin factura (nominas, SS, impuestos, autonomos). No suman IVA deducible. Si alguno deberia llevar factura con IVA, vinculala desde la pestana de Cargos.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FiscalTab;
